import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Tooltip, MenuItem } from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';

import useApi from '../hooks/use-api';
import buildApiRequestConfig from '../api/url-builder';
import routes from '../api/routes';
import { useUser } from '../providers/user-provider';
import { usePermissions } from '../providers/permissions-provider';
import { useToast } from '../providers/toaster-provider';
import { useSettings } from '../providers/settings-provider';
import xeroIcon from '../files/images/xero-icon.webp';
import { useInvoiceHorizonCheck } from './popup/xero-invoice-horizon';
import { usePlanFeature } from '../hooks/use-plan-feature';
import { Lock } from 'lucide-react';

type XeroSyncType = 'gl' | 'transactions' | 'invoices' | 'statuses' | 'clients';

interface XeroIntegration {
    status: 'Pending' | 'Active' | 'Disconnected';
    bankAccountId?: number | null;
    xeroBankAccountId?: string | null;
}

interface SyncHistoryEntry {
    triggeredAt: string | null;
    completedAt: string | null;
    status: 'completed' | 'failed' | 'in_progress' | null;
    detail: string | null;
}

const SYNC_ROUTE_BY_TYPE: Record<XeroSyncType, keyof typeof routes.xero> = {
    gl: 'syncGL',
    transactions: 'syncTransactions',
    invoices: 'syncInvoices',
    statuses: 'syncStatuses',
    clients: 'syncClients',
};

// Maps this button's `type` prop to the corresponding key in
// GET /xero/sync-history/:entityId's response (xero-controller.js's
// getSyncHistory) - already used by the Integrations page, reused here just
// for the "last synced" readout instead of building a second endpoint.
const HISTORY_KEY_BY_TYPE: Record<XeroSyncType, string> = {
    gl: 'lastGLSync',
    transactions: 'lastTransactionSync',
    invoices: 'lastInvoicesSync',
    statuses: 'lastStatusesSync',
    clients: 'lastClientsSync',
};

// While a sync is genuinely still running server-side, poll for its
// completion so the "last synced" readout (and the disabled state) update on
// their own instead of only refreshing on the next full page load.
const POLL_INTERVAL_MS = 8000;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;

// Drop into a page header when that page's data can be synced from Xero.
// Stays invisible until the entity is actually connected, matching the same
// "Sync Now" action already offered per-card on the Integrations page - this
// is just a shortcut to it from the page the synced data actually lives on.
const XeroSyncButton: React.FC<{
    type: XeroSyncType,
    compact?: boolean,
    // Renders as a MenuItem (icon + label, same row shape as its siblings)
    // instead of a standalone Button - for dropping into an existing action
    // menu (e.g. the Transactions page's mobile "+" FAB menu, alongside
    // Import/Provision/Transaction) rather than taking its own spot in a
    // page header.
    asMenuItem?: boolean,
    // Called right when the item is clicked (sync itself is fire-and-forget,
    // same as this menu's other actions) - lets the parent close the menu.
    onClick?: () => void,
}> = ({ type, compact = false, asMenuItem = false, onClick }) => {
    const { user } = useUser();
    const { can } = usePermissions();
    const { showSuccess, showError } = useToast();
    const { formatDate } = useSettings();
    const entityId = user?.entityId;
    const canSync = can('integrations.sync', 'update');

    const [integration, setIntegration] = useState<XeroIntegration | null>(null);
    const [syncing, setSyncing] = useState(false);
    const [syncHistory, setSyncHistory] = useState<SyncHistoryEntry | null>(null);
    const [getResult, doGet] = useApi<any>();
    const [syncResult, doSync] = useApi<any>();
    const [historyResult, doGetHistory] = useApi<any>();
    const pollRef = useRef<{ intervalId?: ReturnType<typeof setInterval>; timeoutId?: ReturnType<typeof setTimeout> }>({});
    // An invoice sync that just finished - say if any invoices were held back past the horizon.
    const { checkInvoiceHorizon, invoiceHorizonDialog } = useInvoiceHorizonCheck();
    // The plan doesn't include Xero (e.g. after a downgrade) - shown locked,
    // clicking opens the upgrade / add-on prompt.
    const { has: hasXero, prompt: promptXero } = usePlanFeature('xeroIntegration');
    const locked = !hasXero;
    const lastStatusRef = useRef<string | null>(null);

    useEffect(() => {
        if (!entityId) return;
        doGet(buildApiRequestConfig(routes.xero.getIntegration, [{ param: 'entityId', value: entityId }]));
    }, [entityId, doGet]);

    useEffect(() => {
        if (getResult.data) {
            const payload = getResult.data.data ?? getResult.data;
            setIntegration(payload.integration ?? payload);
        }
        if (getResult.error) {
            // No integration for this entity (404) - stay hidden.
            setIntegration(null);
        }
    }, [getResult.data, getResult.error]);

    const stopPolling = useCallback(() => {
        if (pollRef.current.intervalId) clearInterval(pollRef.current.intervalId);
        if (pollRef.current.timeoutId) clearTimeout(pollRef.current.timeoutId);
        pollRef.current = {};
    }, []);

    const fetchHistory = useCallback(() => {
        if (!entityId) return;
        doGetHistory(buildApiRequestConfig(routes.xero.syncHistory, [{ param: 'entityId', value: entityId }]));
    }, [entityId, doGetHistory]);

    // "Last synced" readout - fetched on mount so it's visible before the
    // user ever clicks Sync, not just after triggering one this session.
    useEffect(() => {
        fetchHistory();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entityId]);

    useEffect(() => {
        if (!historyResult.data) return;
        const payload = historyResult.data.data ?? historyResult.data;
        const entry: SyncHistoryEntry | null = payload?.[HISTORY_KEY_BY_TYPE[type]] ?? null;
        setSyncHistory(entry);
        if (type === 'invoices' && lastStatusRef.current === 'in_progress' && entry?.status === 'completed') checkInvoiceHorizon();
        lastStatusRef.current = entry?.status ?? null;
        // Still running server-side - keep polling until it lands on a
        // terminal status so the tooltip/disabled state update on their own.
        if (entry?.status === 'in_progress') {
            if (!pollRef.current.intervalId) {
                pollRef.current.intervalId = setInterval(fetchHistory, POLL_INTERVAL_MS);
                pollRef.current.timeoutId = setTimeout(stopPolling, POLL_TIMEOUT_MS);
            }
        } else {
            stopPolling();
        }
    }, [historyResult.data, type, fetchHistory, stopPolling, checkInvoiceHorizon]);

    useEffect(() => stopPolling, [stopPolling]);

    useEffect(() => {
        if (!syncing) return;
        if (syncResult.data) {
            setSyncing(false);
            showSuccess('Sync started');
            fetchHistory();
        }
        if (syncResult.error) {
            setSyncing(false);
            showError(typeof syncResult.error === 'string' ? syncResult.error : 'Failed to start sync');
        }
    }, [syncResult.data, syncResult.error, syncing, showSuccess, showError, fetchHistory]);

    if (!entityId || !integration || integration.status !== 'Active') return null;

    const needsBankAccount = type === 'transactions' || type === 'invoices';
    const missingBankAccount = type === 'transactions'
        ? (!integration.bankAccountId || !integration.xeroBankAccountId)
        : type === 'invoices'
            ? !integration.bankAccountId
            : false;

    // syncHistory's own status catches a sync still running from a previous
    // page visit (or one this button's own trigger just kicked off) - not
    // just the brief window this component's local `syncing` flag covers
    // between clicking and the trigger request itself resolving.
    const isRunning = syncing || syncHistory?.status === 'in_progress';
    const disabled = !locked && (isRunning || !canSync || (needsBankAccount && missingBankAccount));

    const lastSyncedLabel = syncHistory?.completedAt
        ? `Last synced: ${formatDate(syncHistory.completedAt, true)}`
        : syncHistory?.status === 'in_progress'
            ? 'Sync in progress…'
            : syncHistory?.status === 'failed'
                ? 'Last sync failed'
                : 'Never synced';
    const disabledReason = !canSync
        ? undefined
        : (needsBankAccount && missingBankAccount)
            ? 'Select a bank account on the Integrations page first'
            : undefined;
    // Always show something on hover - the actionable reason it's disabled
    // when there is one, otherwise when the last sync actually completed, so
    // a user can tell a sync finished without needing to reload the page.
    const tooltip = locked ? 'Please upgrade to access' : (disabledReason || lastSyncedLabel);

    const handleClick = () => {
        onClick?.();
        if (locked) { promptXero(); return; }
        if (disabled) return;
        setSyncing(true);
        const body = type === 'transactions'
            ? { bankAccountId: integration.bankAccountId, xeroBankAccountId: integration.xeroBankAccountId }
            : type === 'invoices'
                ? { bankAccountId: integration.bankAccountId }
                : undefined;
        doSync({
            ...buildApiRequestConfig(routes.xero[SYNC_ROUTE_BY_TYPE[type]], [{ param: 'entityId', value: entityId }]),
            body,
        });
    };

    if (asMenuItem) {
        return (
            <>
            {invoiceHorizonDialog}
            <MenuItem disabled={disabled} onClick={handleClick} sx={{ display: 'flex', gap: 1, opacity: locked ? 0.5 : 1 }}>
                <img src={xeroIcon} alt="Xero" style={{ width: 16, height: 16 }} />
                {isRunning ? 'Syncing...' : 'Sync'}
            </MenuItem>
            </>
        );
    }

    // Compact mode (a 40px icon-only button, beside the Transactions and
    // Categories page header buttons) is opt-in; everywhere else this is a
    // normal labeled button.
    if (!compact) {
        const button = (
            <Button
                variant="outlined"
                color="primary"
                disabled={disabled}
                onClick={handleClick}
                startIcon={<img src={xeroIcon} alt="Xero" style={{ width: 30, height: 30 }} />}
                endIcon={locked ? <Lock size={14} /> : undefined}
                sx={{
                    color: 'var(--color-primary)',
                    opacity: locked ? 0.5 : 1,
                    height: 34,
                    borderColor: 'var(--color-primary)',
                    '&:hover': { backgroundColor: 'rgba(var(--color-primary-rgb), 0.0863)' },
                }}
            >
                {isRunning ? 'Syncing...' : 'Sync'}
            </Button>
        );
        // A disabled Button doesn't fire pointer events, so Tooltip needs a
        // listenable wrapper - kept even when enabled so the "last synced"
        // readout is reachable on hover either way.
        return <>{invoiceHorizonDialog}<Tooltip title={tooltip}><Box component="span">{button}</Box></Tooltip></>;
    }

    // The action and the "last synced" readout (or, when disabled, the reason
    // why) are on the tooltip - the wrapping span keeps it reachable on a
    // disabled Button.
    return (
        <>
        {invoiceHorizonDialog}
        <Tooltip title={locked ? tooltip : <>{isRunning ? 'Syncing...' : 'Sync with Xero'}{tooltip ? <><br />{tooltip}</> : null}</>}>
            <Box component="span">
                <Button
                    variant="outlined"
                    color="primary"
                    disabled={disabled}
                    onClick={handleClick}
                    sx={{
                        color: 'var(--color-primary)',
                        opacity: locked ? 0.5 : 1,
                        height: 40,
                        minWidth: 40,
                        px: 1.25,
                        display: 'flex',
                        alignItems: 'center',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        borderColor: 'var(--color-primary)',
                        '&:hover': { backgroundColor: 'rgba(var(--color-primary-rgb), 0.0863)' },
                    }}
                >
                    <img src={xeroIcon} alt="Xero" style={{ width: 30, height: 30 }} />
                </Button>
            </Box>
        </Tooltip>
        </>
    );
};

export default XeroSyncButton;
